import { z } from 'zod';
import { COOKIE_NAME_DRAFT_MODE } from '~/types/cookieNames';
import { DRAFT_COOKIE_OPTIONS } from '~/types/cookieOptions';
import { POPUP_AUTH_COMPLETE_MESSAGE_TYPE, POPUP_LOGOUT_COMPLETE_MESSAGE_TYPE } from '~/utils/auth/popupAuth';
import log from '~/utils/log';

const POPUP_WINDOW_NAME = 'portal-auth-popup';
const POPUP_FEATURES = 'popup=yes,width=520,height=720';
const LOGOUT_REQUEST_TIMEOUT = 10000;
const LOGOUT_POPUP_TIMEOUT = 20000;

const popupAuthMessageSchema = z.object({
    type: z.literal(POPUP_AUTH_COMPLETE_MESSAGE_TYPE),
    exchangeCode: z.string()
});

const popupLogoutMessageSchema = z.object({
    type: z.literal(POPUP_LOGOUT_COMPLETE_MESSAGE_TYPE)
});

type PopupAuthMessage = z.infer<typeof popupAuthMessageSchema>;
type LogoutJsonResponse = {
    keycloakLogoutUrl: string | null;
    redirectTo: string;
};

const isPopupAuthMessage = (data: unknown): data is PopupAuthMessage => popupAuthMessageSchema.safeParse(data).success;

const isPopupLogoutMessage = (data: unknown) => popupLogoutMessageSchema.safeParse(data).success;

const completeIframeAuth = async (exchangeCode: string) => {
    await $fetch('/api/auth/iframe-complete', {
        method: 'POST',
        body: { code: exchangeCode },
        credentials: 'include'
    });
};

const throwIfNotBrowser = () => {
    if (!import.meta.client) {
        throw new Error('useAuthPopup can only be used in the browser');
    }
};

export const useAuthPopup = () => {
    const runtimeConfig = useRuntimeConfig();

    const openAuthPopup = (url: string) =>
        new Promise<void>((resolve, reject) => {
            throwIfNotBrowser();
            const rawBaseUrl = runtimeConfig.public.baseUrl;
            if (!rawBaseUrl) {
                reject(new Error('Missing base URL for iframe auth popup'));
                return;
            }
            const expectedOrigin = new URL(rawBaseUrl).origin;

            const popup = window.open(url, POPUP_WINDOW_NAME, POPUP_FEATURES);
            if (!popup) {
                reject(new Error('Failed to open auth popup. Please allow popups for this site.'));
                return;
            }

            let settled = false;

            const cleanup = () => {
                window.removeEventListener('message', handleMessage);
                window.clearInterval(popupMonitor);
            };

            const finish = async (exchangeCode: string) => {
                if (settled) return;
                settled = true;
                cleanup();

                try {
                    await completeIframeAuth(exchangeCode);
                    resolve();
                } catch (error) {
                    reject(error);
                }
            };

            const fail = (error: Error) => {
                if (settled) return;
                settled = true;
                cleanup();
                reject(error);
            };

            const handleMessage = (event: MessageEvent) => {
                if (event.origin !== expectedOrigin) return;
                if (event.source !== popup) return;
                if (!isPopupAuthMessage(event.data)) return;

                void finish(event.data.exchangeCode);
            };

            const popupMonitor = window.setInterval(() => {
                if (popup.closed) {
                    fail(new Error('Auth popup was closed before login completed.'));
                }
            }, 500);

            window.addEventListener('message', handleMessage);
        });

    const navigateAfterAuth = (redirectTo: string) => {
        const redirectUrl = new URL(redirectTo, window.location.origin);
        if (redirectUrl.origin !== window.location.origin) {
            window.location.reload();
            return;
        }

        const redirectPath = `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
        log.debug('[useAuthPopup.ts] Redirecting to', redirectPath);
        window.location.href = redirectPath;
    };

    const loginWithPopup = async (redirectTo: string, options?: { method?: 'doccheck' }) => {
        throwIfNotBrowser();

        const urlSearchParams = new URLSearchParams();
        urlSearchParams.set('redirect_to', redirectTo);
        urlSearchParams.set('popup', '1');

        if (options?.method === 'doccheck') {
            urlSearchParams.set('method', 'doccheck');
        }

        const draftModeCookie = useCookie<string | undefined>(COOKIE_NAME_DRAFT_MODE, DRAFT_COOKIE_OPTIONS);
        if (draftModeCookie.value) {
            urlSearchParams.set('draft_token', draftModeCookie.value);
        }

        const url = `/api/auth/login?${urlSearchParams.toString()}`;

        await openAuthPopup(url);
        navigateAfterAuth(redirectTo);
    };

    const logoutWithIframeFlow = async (redirectTo: string) => {
        throwIfNotBrowser();
        const response = await $fetch<LogoutJsonResponse>('/api/auth/logout', {
            query: {
                redirect_to: redirectTo,
                json: '1'
            },
            credentials: 'include',
            timeout: LOGOUT_REQUEST_TIMEOUT
        });

        if (response.keycloakLogoutUrl) {
            const popup = window.open(response.keycloakLogoutUrl, POPUP_WINDOW_NAME, POPUP_FEATURES);

            if (!popup) {
                alert('Failed to open popup. Please allow popups for this site and try again.');
            } else {
                await waitForPopupLogout(popup);
            }
        }

        if (!response.redirectTo) return;

        navigateAfterAuth(response.redirectTo);
    };

    const waitForPopupLogout = (popup: Window) =>
        new Promise<void>((resolve) => {
            const rawBaseUrl = runtimeConfig.public.baseUrl;
            if (!rawBaseUrl) {
                resolve();
                return;
            }

            const expectedOrigin = new URL(rawBaseUrl).origin;
            let settled = false;

            const finish = () => {
                if (settled) return;
                settled = true;
                window.removeEventListener('message', handleMessage);
                window.clearInterval(popupMonitor);
                window.clearTimeout(timeoutId);
                if (!popup.closed) popup.close();
                resolve();
            };

            const handleMessage = (event: MessageEvent) => {
                if (event.origin !== expectedOrigin) return;
                if (event.source !== popup) return;
                if (!isPopupLogoutMessage(event.data)) return;
                finish();
            };

            const popupMonitor = window.setInterval(() => {
                if (popup.closed) finish();
            }, 500);

            const timeoutId = window.setTimeout(finish, LOGOUT_POPUP_TIMEOUT);

            window.addEventListener('message', handleMessage);
        });

    return {
        loginWithPopup,
        logoutWithIframeFlow
    };
};
